import { memo, useEffect, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { usePlatforms } from '@mint-lib/routing-context';
import { useTheme } from '@mint-lib/styled';
import type {} from '@mint-lib/theme/src/extensions/palette.js';
import { processSxProps } from '@mint-lib/tw-utils';
import uiShell from '@mint-lib/ui-shell';

import { snakeCaseToSpace } from '@myn/shared/src/utils';

import GoBackLink from '../../components/_atoms/GoBackLink/index.jsx';
import IntegrationLogoWithType from '../../components/_atoms/IntegrationLogoWithType/IntegrationLogoWithType.jsx';
import AccountsTab from '../../components/_molecules/AccountsTab/AccountsTab.jsx';
import IntegrationsTabs from '../../components/_molecules/IntegrationsTabs/index.js';
import NonIntegratedInfo from '../../components/_molecules/NonIntegratedInfo/NonIntegratedInfo.jsx';
import { EmailRecosForm } from '../../components/_organisms/EmailRecos/index.js';
import OverviewTab from '../../components/features/OverviewTab/OverviewTab.jsx';
import { NotificationTabs } from '../../constants/Tabs.jsx';
import { useIntegrationsTabs } from '../../hooks//index.js';
import {
  useIntegrationLayoutRouteApi,
  useIntegrationPaths,
  useIntegrationsIndexRouteApi,
} from '../../hooks/useIntegrationsRouteApi.js';
import { useSelectedPlatform } from '../../hooks/useSelectedPlatform.js';
import { PlatformName } from '../../types/index.js';
import { isVirtualPlatformIntegrated } from '../../utils/platforms.js';
import SkeletonMarkup from './SkeletonMarkup.jsx';

const IntegrationPage = () => {
  const { t } = useTranslation('@myn/integrations_v2');
  const theme = useTheme();

  const layoutRoute = useIntegrationLayoutRouteApi();
  const indexRoute = useIntegrationsIndexRouteApi();
  const navigateToTab = layoutRoute.useNavigate();
  const navigateToIndex = indexRoute.useNavigate();
  const paths = useIntegrationPaths();
  const search = layoutRoute.useSearch();
  const { integration: routeIntegration, tabName } = layoutRoute.useParams();

  const integration = useMemo(
    () => (routeIntegration === 'ga4' ? 'google_analytics' : routeIntegration),
    [routeIntegration],
  ) as PlatformName;

  const selectedPlatform = useSelectedPlatform();

  const { tabs: integrationTabs, initialTab } =
    useIntegrationsTabs(selectedPlatform);

  const { platformByLabel } = usePlatforms({ excludeDeleted: true });

  const virtualPlatformIsIntegrated =
    isVirtualPlatformIntegrated(selectedPlatform);

  const showNonIntegratedInfo =
    selectedPlatform?.type === 'virtual' &&
    !selectedPlatform?.isIntegrated &&
    !virtualPlatformIsIntegrated;

  const goToTab = (tab: NotificationTabs) => {
    const isAccounts = tab === 'accounts';
    navigateToTab({
      to: paths.integrationTypeSlash,
      params: {
        integration,
        tabName: tab,
        type: isAccounts ? 'add' : 'details',
      },
      replace: true,
    });
  };

  useEffect(() => {
    if (
      Object.keys(platformByLabel).length !== 0 &&
      integration !== 'google_analytics' &&
      !platformByLabel[integration]
    ) {
      navigateToIndex({ to: paths.index });
    }
  }, [navigateToIndex, integration, platformByLabel, paths.index]);

  useEffect(() => {
    if (!search.state) {
      return;
    }
  }, []);

  useEffect(() => {
    // * when user is trying to go to non-existing tab
    if (
      Object.keys(platformByLabel).length === 0 ||
      !integrationTabs ||
      integrationTabs.some(({ id }) => id === tabName)
    ) {
      return;
    }

    goToTab(initialTab.id);
  }, [tabName, initialTab, platformByLabel]);

  const onTabClick = (tab: NotificationTabs) => {
    goToTab(tab);
  };

  useEffect(() => {
    uiShell.publish('renderTitle', {
      callback: () =>
        `${t('integrations')} | ${snakeCaseToSpace(
          selectedPlatform?.label ?? integration,
        ).toUpperCase()}`,
    });
  }, [selectedPlatform, integration]);

  const CurrentTab = useMemo(() => {
    switch (tabName) {
      case 'accounts': {
        return <AccountsTab />;
      }
      case 'emailsuggestions': {
        return (
          <EmailRecosForm
            integration={integration}
            platformType={selectedPlatform?.category ?? ''}
          />
        );
      }
      case 'overview': {
        return <OverviewTab platform={selectedPlatform} />;
      }
      default: {
        return <div />;
      }
    }
  }, [tabName, integration, selectedPlatform]);

  return (
    <div data-testid="integration-page-id">
      {!selectedPlatform ? (
        <SkeletonMarkup />
      ) : (
        <>
          <GoBackLink />
          <IntegrationLogoWithType integration={selectedPlatform} />
          {showNonIntegratedInfo && <NonIntegratedInfo />}

          <IntegrationsTabs
            activeTab={tabName as NotificationTabs}
            onTabChange={(tab) => {
              onTabClick(tab);
            }}
            tabList={integrationTabs}
          />
          <div
            className="flex w-full min-w-0 flex-col"
            data-testid={'content-page-id'}
          >
            {!showNonIntegratedInfo && (
              <div
                className="flex w-full min-w-0 flex-col"
                data-testid={'content-page-id'}
              >
                <div
                  {...processSxProps({
                    sx: {
                      borderTop: `1px solid ${selectedPlatform ? theme.palette.greyscale.grey20 : 'none'}`,
                    },
                    className: 'relative flex overflow-hidden',
                  })}
                  style={{ height: 'calc(100vh - 240px)' }}
                >
                  {CurrentTab}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(IntegrationPage);
